/**
 * The Connect a bank stepper on the Banking page (piece 24; decision 1770):
 * five steps, each a done test read from rows the plan already names, and
 * whether the step can be reached yet. 🔒 Nothing here is stored.
 */
import { MATCH_TIER_KEYS } from './bankMatching';

export const BANK_STEPS_PATH = '/banking/connect-steps';

export const BANK_STEPS = ['keys', 'account', 'firstPull', 'tiers', 'reconciled'] as const;
export type BankStep = (typeof BANK_STEPS)[number];

/** What the server counts; every field is a read of a saved row and none is stored for the stepper. */
export interface BankStepsView {
  /** The Plaid keys held on Settings › Integrations, the fact the Plaid setup page reads. */
  keysHeld: boolean;
  /** Active `BankAccount` rows carrying a feed connection. */
  connectedAccounts: number;
  /** `BankTransaction` rows on those connected accounts. */
  pulledLines: number;
  /** How many of the three match-tier `SettingValue` rows carry a saved value. */
  tiersSaved: number;
  /** `BankReconciliation` rows in the Completed status. */
  completedReconciliations: number;
}

export interface BankStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 Each step's done test (decision 1770). The keys are held when both of
 * Plaid's keys are; an account is connected when an active one carries a feed
 * connection; the first pull is in when a connected account holds a line; the
 * tiers are set when all three rows carry a saved value, a default that was
 * never saved counting as not set; the first reconciliation is closed when one
 * is Completed. A key the answer lacks reads as not done.
 */
export function bankSteps(view: Partial<BankStepsView>): Record<BankStep, BankStepFacts> {
  const keys = view.keysHeld === true;
  const connected = (view.connectedAccounts ?? 0) > 0;
  const pulled = (view.pulledLines ?? 0) > 0;
  return {
    keys: { done: keys, reachable: true },
    account: { done: connected, reachable: keys || connected },
    firstPull: { done: pulled, reachable: connected },
    tiers: { done: (view.tiersSaved ?? 0) >= MATCH_TIER_KEYS.length, reachable: true },
    reconciled: { done: (view.completedReconciliations ?? 0) > 0, reachable: pulled },
  };
}
